import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Stack, Button, Grid, CircularProgress, Alert, Modal, IconButton } from '@mui/material';
import { Video, Globe, Camera, Utensils, MessageCircle, X, Play } from 'lucide-react';
import { getRetreatMedia, getRetreatMediaGallery } from '../../../Services/Retreats/RetreatApis';

const MediaTab = ({ retreat }) => {
    const [retreatMedia, setRetreatMedia] = useState(null);
    const [retreatMediaGallery, setRetreatMediaGallery] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewVideo, setPreviewVideo] = useState(null);

    const handleOpenVideo = (videoUrl) => {
        setPreviewVideo(videoUrl);
    };

    const handleCloseVideo = () => {
        setPreviewVideo(null);
    };

    // Ensure URL has a protocol so the browser opens it as an external link,
    // not as a relative path (e.g. "www.youtube.com/..." → "https://www.youtube.com/...")
    const normalizeUrl = (url) => {
        if (!url) return url;
        if (/^https?:\/\//i.test(url)) return url;
        return `https://${url}`;
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const [mediaRes, galleryRes] = await Promise.all([
                    getRetreatMedia(retreat.id),
                    getRetreatMediaGallery(retreat.id)
                ]);

                if (mediaRes && mediaRes.success) setRetreatMedia(mediaRes.data);
                if (galleryRes && galleryRes.success) setRetreatMediaGallery(galleryRes.data);

                if (!mediaRes?.success && !galleryRes?.success) {
                    setError("Failed to fetch media information");
                }
            } catch (err) {
                console.error("Error fetching retreat media:", err);
                setError("An error occurred while fetching media information");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [retreat?.id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
    }

    if (!retreatMedia && !retreatMediaGallery) {
        return <Typography>No media information available.</Typography>;
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Media & Gallery
            </Typography>

            {/* Media Links Section */}
            {retreatMedia && (
                <Paper sx={{ p: 3, mb: 3, bgcolor: '#F8F9FA' }} elevation={0}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        <Video size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                        Media Links
                    </Typography>
                    <Stack spacing={2}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                            --  Available Media Links  --
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            {retreatMedia.videoLink && (
                                <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                    variant="outlined"
                                    size="small"
                                    href={normalizeUrl(retreatMedia.videoLink)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<Video size={16} />}
                                >
                                    Video
                                </Button>
                            )}

                            {retreatMedia.retreatWebsiteUrl && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    href={normalizeUrl(retreatMedia.retreatWebsiteUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<Globe size={16} />}
                                >
                                    Website
                                </Button>
                            )}

                            {retreatMedia.facebookEventUrl && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    href={normalizeUrl(retreatMedia.facebookEventUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Facebook
                                </Button>
                            )}

                            {retreatMedia.instagramUrl && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    href={normalizeUrl(retreatMedia.instagramUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Instagram
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Paper>
            )}


            {/* Gallery Section */}
            {retreatMediaGallery && retreatMediaGallery.length > 0 && (
                <Paper sx={{ p: 3, bgcolor: '#F8F9FA' }} elevation={0}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        <Camera size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                        Gallery
                    </Typography>

                    {/* Group by media type */}
                    <Box>
                        {/* Photos */}
                        {retreatMediaGallery.filter(item => item.mediaType === 'photo').length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                                    <Camera size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                                    Photos
                                </Typography>
                                <Grid container spacing={2}>
                                    {retreatMediaGallery
                                        .filter(item => item.mediaType === 'photo')
                                        .map((item, index) => (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        bgcolor: '#f5f5f5',
                                                        aspectRatio: '16/9',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            '& img': {
                                                                transform: 'scale(1.05)'
                                                            }
                                                        }
                                                    }}
                                                    onClick={() => window.open(item.fileUrl, '_blank')}
                                                >
                                                    <img
                                                        src={item.fileUrl}
                                                        alt={`Gallery item ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            transition: 'transform 0.3s ease'
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                </Grid>
                            </Box>
                        )}

                        {/* Food Photos */}
                        {retreatMediaGallery.filter(item => item.mediaType === 'food_photo').length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                                    <Utensils size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                                    Food Photos
                                </Typography>
                                <Grid container spacing={2}>
                                    {retreatMediaGallery
                                        .filter(item => item.mediaType === 'food_photo')
                                        .map((item, index) => (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        bgcolor: '#f5f5f5',
                                                        aspectRatio: '16/9',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            '& img': {
                                                                transform: 'scale(1.05)'
                                                            }
                                                        }
                                                    }}
                                                    onClick={() => window.open(item.fileUrl, '_blank')}
                                                >
                                                    <img
                                                        src={item.fileUrl}
                                                        alt={`Food item ${index + 1}`}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            transition: 'transform 0.3s ease'
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                </Grid>
                            </Box>
                        )}
                        {/* Videos */}
                        {retreatMediaGallery.filter(item => item.mediaType === 'video').length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: '#333', display: 'flex', alignItems: 'center' }}>
                                    <Video size={16} style={{ marginRight: 8, color: '#1976D2' }} />
                                    Gallery Videos
                                </Typography>
                                <Grid container spacing={2}>
                                    {retreatMediaGallery
                                        .filter(item => item.mediaType === 'video')
                                        .map((item, index) => (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        borderRadius: '12px',
                                                        overflow: 'hidden',
                                                        bgcolor: '#000',
                                                        aspectRatio: '16/9',
                                                        cursor: 'pointer',
                                                        border: '1px solid #EDEDED',
                                                        '&:hover': {
                                                            '& .play-overlay': { opacity: 1 },
                                                            '& video': { transform: 'scale(1.05)', opacity: 0.7 }
                                                        }
                                                    }}
                                                    onClick={() => handleOpenVideo(item.fileUrl)}
                                                >
                                                    <Box
                                                        component="video"
                                                        src={`${item.fileUrl}#t=0.1`}
                                                        preload="metadata"
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            transition: 'all 0.3s ease',
                                                            opacity: 0.8
                                                        }}
                                                    />
                                                    <Box
                                                        className="play-overlay"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0, left: 0, right: 0, bottom: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: 'rgba(0,0,0,0.2)',
                                                            transition: 'opacity 0.3s ease',
                                                            opacity: 0.8
                                                        }}
                                                    >
                                                        <Box sx={{
                                                            width: 50, height: 50,
                                                            borderRadius: '50%',
                                                            bgcolor: 'rgba(255,255,255,0.9)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                                        }}>
                                                            <Play size={24} fill="#1976D2" color="#1976D2" style={{ marginLeft: 3 }} />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        bottom: 0, left: 0, right: 0,
                                                        p: 1.5,
                                                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                                        color: 'white'
                                                    }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                            Video {index + 1}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                </Grid>
                            </Box>
                        )}

                        {/* Video Reviews */}
                        {retreatMediaGallery.filter(item => item.mediaType === 'video_review').length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: '#333', display: 'flex', alignItems: 'center' }}>
                                    <MessageCircle size={16} style={{ marginRight: 8, color: '#C62828' }} />
                                    Video Reviews
                                </Typography>
                                <Grid container spacing={2}>
                                    {retreatMediaGallery
                                        .filter(item => item.mediaType === 'video_review')
                                        .map((item, index) => (
                                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        borderRadius: '12px',
                                                        overflow: 'hidden',
                                                        bgcolor: '#000',
                                                        aspectRatio: '16/9',
                                                        cursor: 'pointer',
                                                        border: '1px solid #EDEDED',
                                                        '&:hover': {
                                                            '& .play-overlay': { opacity: 1 },
                                                            '& video': { transform: 'scale(1.05)', opacity: 0.7 }
                                                        }
                                                    }}
                                                    onClick={() => handleOpenVideo(item.fileUrl)}
                                                >
                                                    <Box
                                                        component="video"
                                                        src={`${item.fileUrl}#t=0.1`}
                                                        preload="metadata"
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            transition: 'all 0.3s ease',
                                                            opacity: 0.8
                                                        }}
                                                    />
                                                    <Box
                                                        className="play-overlay"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0, left: 0, right: 0, bottom: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: 'rgba(0,0,0,0.2)',
                                                            transition: 'opacity 0.3s ease',
                                                            opacity: 0.8
                                                        }}
                                                    >
                                                        <Box sx={{
                                                            width: 50, height: 50,
                                                            borderRadius: '50%',
                                                            bgcolor: 'rgba(255,255,255,0.9)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                                        }}>
                                                            <Play size={24} fill="#C62828" color="#C62828" style={{ marginLeft: 3 }} />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        bottom: 0, left: 0, right: 0,
                                                        p: 1.5,
                                                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                                        color: 'white'
                                                    }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                            Review {index + 1}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </Paper>
            )}
            {/* Video Player Modal */}
            <Modal
                open={!!previewVideo}
                onClose={handleCloseVideo}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(0,0,0,0.85)'
                }}
            >
                <Box sx={{
                    position: 'relative',
                    width: '90vw',
                    maxWidth: '1000px',
                    aspectRatio: '16/9',
                    bgcolor: '#000',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                    outline: 'none'
                }}>
                    <IconButton
                        onClick={handleCloseVideo}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'white',
                            bgcolor: 'rgba(0,0,0,0.3)',
                            zIndex: 10
                        }}
                    >
                        <X size={24} />
                    </IconButton>

                    {previewVideo && (
                        <Box
                            component="video"
                            src={previewVideo}
                            controls
                            autoPlay
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'block'
                            }}
                        />
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default MediaTab;
